import {QueryStatus, QuerySubState} from '@reduxjs/toolkit/dist/query'
import {
  getAccommodationPageHotelId,
  getIsAlternativeOffersOverlayManuallyClosed,
  getIsLastOfferCheckFailed
} from 'modules/accommodation/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {getTopOffersParameters} from 'modules/searchApi/selectors'
import {createSelector} from 'reselect'
import {RootState} from 'store'

import isBofhOffer from '@daedalus/core/src/offer/business/isBofhOffer'
import {offerCheckApi} from '@daedalus/core/src/offer/services/offerCheckApi'
import {
  OffersState,
  searchApi
} from '@daedalus/core/src/sapi/services/searchApi'

export const filterOutVioOffers = (offers: SearchOffer[]) =>
  offers.filter(offer => !isBofhOffer(offer))

export const getAlternativeOffersQueryParams = createSelector(
  [getTopOffersParameters],
  topOffersParameters => {
    return {
      ...topOffersParameters,
      getAllOffers: true
    }
  }
)

const getOffersQuery = (state: RootState) => {
  const hotelId = getAccommodationPageHotelId(state)
  const params = getAlternativeOffersQueryParams(state, hotelId)

  return searchApi.endpoints.getOffers.select(params)(state)
}

const getOffersQueryData = createSelector(
  [getOffersQuery],
  ({status, data}) => {
    if (status === QueryStatus.fulfilled) {
      return data
    }
  }
)

const getOffersEntity = createSelector(
  [getOffersQueryData],
  data => data?.offerEntity
)

/**
 * Gets SAPI.offers endpoint request state (not rtkq query status)
 * @returns (SAPI)OffersState
 */
export const getAlternativeOffersLoadingState = createSelector(
  [getOffersQueryData],
  data => data?.status
)

export const getOffersEntityWithoutVioOffers = createSelector(
  [getOffersEntity],
  offersEntity => ({
    ...offersEntity,
    offers: filterOutVioOffers(offersEntity?.offers || [])
  })
)

/**
 * Gets filtered (only non-bofh), first 3 offers
 */
export const getAlternativeOffersList = createSelector(
  [getOffersEntity],
  offersEntity => filterOutVioOffers(offersEntity?.offers || []).slice(0, 3)
)

export const getIsAnyOfferCheckCallStarted = state =>
  Object.values(
    state[offerCheckApi.reducerPath].queries as QuerySubState<undefined>[]
  ).some(query =>
    [QueryStatus.pending, QueryStatus.uninitialized].includes(query.status)
  )

export const getIsOfferCheckLoadingOverlayVisible = createSelector(
  [getIsAnyOfferCheckCallStarted, getAlternativeOffersLoadingState],
  (isAnyOfferCheckCallPending, alternativeOffersLoadingState) =>
    isAnyOfferCheckCallPending ||
    alternativeOffersLoadingState == OffersState.Pending
)

export const getIsAnyOfferCheckFailed = state =>
  Object.values(
    state[offerCheckApi.reducerPath].queries as QuerySubState<undefined>[]
  ).some(query => query.error !== undefined)

/**
 * Open when:
 * - no pending checks
 * - last check failes
 * - overlay is not closed manually
 * - there are any offers available
 */
export const getIsAlternativeOffersOverlayOpen = createSelector(
  [
    getIsAnyOfferCheckCallStarted,
    getIsAlternativeOffersOverlayManuallyClosed,
    getIsLastOfferCheckFailed,
    getAlternativeOffersList
  ],
  (
    isAnyOfferCheckCallPending,
    isAlternativeOffersOverlayManuallyClosed,
    isLastOfferCheckFailed,
    alternativeOffersList
  ) =>
    !isAnyOfferCheckCallPending &&
    !isAlternativeOffersOverlayManuallyClosed &&
    isLastOfferCheckFailed &&
    alternativeOffersList.length > 0
)
